import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogActions, DialogContent, Typography } from "@mui/material"
import { WarningOutlined } from "@mui-symbols-material/w300"
import React, { useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import {
  type OverrideTestResultsFormData,
  OverrideTestResultsSchema,
  type ProductTestResultFormValues,
} from "../../schemas"

import { Button } from "@/components/common/Button"
import type { ProductTestResult } from "@/graphql/codegen/graphql"
import { ProductTestResultStatus } from "@/graphql/codegen/graphql"
import OverrideTestResultsTable from "@/screens/Products/components/Product/Quality/TestResults/components/OverrideTestResultsDialog/OverrideTestResultsTable"
import { ProductTestDocumentsForm } from "@/screens/Products/components/Product/Quality/TestResults/components/ProductTestDocumentsForm"
import { ProductTestVersionUpdateDialog } from "@/screens/Products/components/Product/Quality/TestResults/components/ProductTestVersionUpdateDialog"
import { QualityApprovalDenyDialog } from "@/screens/Products/components/Product/Quality/TestResults/components/QualityApprovalDenyDialog"
import {
  useApproveQualityApproval,
  useDenyQualityApproval,
  useUpdateProductTest,
} from "@/screens/Products/components/Product/Quality/TestResults/hooks"
import {
  ProductTestDiffType,
  type ProductTestWithDocuments,
} from "@/screens/Products/components/Product/Quality/TestResults/types"

interface OverrideTestResultsDialogProps {
  onClose: () => void
  productTestResult: ProductTestResult
  productTest: ProductTestWithDocuments
  qualityApprovalId?: string
  onSubmit: () => void
}

const OverrideTestResultsDialog: React.FC<OverrideTestResultsDialogProps> = ({
  onClose,
  productTestResult,
  productTest,
  qualityApprovalId,
  onSubmit,
}) => {
  const { updateProductTest, isUpdating } = useUpdateProductTest()
  const { approveQualityApproval, isApproving } = useApproveQualityApproval()
  const { denyQualityApproval, isDenying } = useDenyQualityApproval()
  const [isVersionUpdateConfirmationOpen, setIsVersionUpdateConfirmationOpen] = useState(false)
  const [isDenyConfirmationOpen, setIsDenyConfirmationOpen] = useState(false)
  const methods = useForm<OverrideTestResultsFormData>({
    resolver: zodResolver(OverrideTestResultsSchema),
    defaultValues: {
      documents: [],
    },
  })
  const { control, watch, reset, formState } = methods
  const documents = watch("documents")
  const isSubmitting = isUpdating || isApproving

  const handleOverrideResults = async (isLotUpdated: boolean) => {
    const documentsToUpload = documents.filter((document) => document.file && document.documentType)
    const documentsToCopy = productTest.documents
    const updatedTestResults =
      productTest.results?.map((result) => {
        const isUpdatedResult = result.productTestResultId === productTestResult.productTestResultId

        return {
          productTestResultId: result.productTestResultId,
          value: result.value,
          ...(isUpdatedResult && { status: ProductTestResultStatus.UpdatedResultsWithinSpec }),
        } as ProductTestResultFormValues
      }) || []

    const isSuccess = qualityApprovalId
      ? await approveQualityApproval({ qualityApprovalId, isLotUpdated }, { documentsToUpload, documentsToCopy })
      : await updateProductTest(
          {
            productTest: {
              results: updatedTestResults,
              manufacturingDate: productTest.manufacturingDate,
              expirationDate: productTest.expirationDate,
            },
            documentsToUpload,
            documentsToDelete: [],
            documentsToCopy,
          },
          productTest.productTestId,
          isLotUpdated
        )

    setIsVersionUpdateConfirmationOpen(false)

    if (!isSuccess) return

    onSubmit()
  }

  const handleClose = () => {
    reset()
    onClose?.()
  }

  const handleDenyConfirm = async (denialReason: string) => {
    if (!qualityApprovalId) return

    const isSuccess = await denyQualityApproval({ qualityApprovalId, denialReason })

    if (isSuccess) {
      setIsDenyConfirmationOpen(false)
      onSubmit()
    }
  }

  if (isVersionUpdateConfirmationOpen) {
    return (
      <ProductTestVersionUpdateDialog
        onClose={() => setIsVersionUpdateConfirmationOpen(false)}
        diffType={ProductTestDiffType.TestResults}
        onSubmit={handleOverrideResults}
        isSubmitting={isSubmitting}
      />
    )
  }

  if (isDenyConfirmationOpen) {
    return (
      <QualityApprovalDenyDialog
        onClose={() => setIsDenyConfirmationOpen(false)}
        onConfirm={handleDenyConfirm}
        isSubmitting={isDenying}
      />
    )
  }

  return (
    <Dialog open onClose={handleClose} data-testid='override-test-results-dialog' maxWidth='md' fullWidth>
      <FormProvider {...methods}>
        <DialogContent className='flex flex-col gap-6 p-10'>
          <Box className='flex flex-col items-center gap-4'>
            <WarningOutlined className='size-[48px] text-red-600' />
            <Typography variant='h5' color='primary' align='center' className='text-2xl font-normal -tracking-wide'>
              Override Test Results
            </Typography>
          </Box>

          <Typography className='mb-5 text-center text-base -tracking-wide text-gray-700'>
            The test results are outside the established limits. Would you like to proceed with approval anyway?
          </Typography>

          <OverrideTestResultsTable productTestResults={productTestResult} />
          <ProductTestDocumentsForm productTestDocuments={[]} title='Supporting Documents' control={control} />
        </DialogContent>

        <DialogActions className='justify-between px-10 pb-10 pt-0'>
          <Button data-testid='cancel-button' appearance='outlined' onClick={handleClose}>
            Cancel
          </Button>
          <Box className='flex items-center gap-x-4'>
            {qualityApprovalId && (
              <Button
                data-testid='deny-button'
                variant='error'
                onClick={() => setIsDenyConfirmationOpen(true)}
                disabled={isSubmitting}
              >
                Deny Test
              </Button>
            )}
            <Button
              data-testid='approve-button'
              variant='primary'
              onClick={() => setIsVersionUpdateConfirmationOpen(true)}
              disabled={!formState.isValid || isDenying || isSubmitting}
            >
              Approve Test
            </Button>
          </Box>
        </DialogActions>
      </FormProvider>
    </Dialog>
  )
}

export default OverrideTestResultsDialog
